/**
 * The strip of parameter sliders under a figure.
 *
 * Dragging one re-evaluates the expressions, so this is where a figure stops
 * being an illustration and becomes the experiment — which is worth a real
 * slider with a keyboard-reachable thumb rather than a bare range input.
 */

import type { PlotSpec } from "../../shared/plot-spec.ts";
import { paramStep } from "./geometry.ts";
import { formatValue } from "./format.ts";
import { MathText } from "../markdown/MathText.tsx";
import { Button } from "../components/ui/button.tsx";
import { Slider } from "../components/ui/slider.tsx";
import { Label } from "../components/ui/label.tsx";

interface PlotControlsProps {
  readonly params: PlotSpec["params"];
  readonly values: Readonly<Record<string, number>>;
  readonly onChange: (name: string, value: number) => void;
  readonly onReset: () => void;
}

export function PlotControls({
  params,
  values,
  onChange,
  onReset,
}: PlotControlsProps) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-md border bg-muted px-4 py-3 font-sans text-sm">
      {params.map((param) => {
        const id = `plot-param-${param.name}`;
        const value = values[param.name] ?? param.value;

        return (
          /* Below `sm` each parameter takes the full row and its slider grows
             into it — three 128px sliders wrapping mid-label on a phone is how
             a live figure stops being draggable. */
          <div
            key={param.name}
            className="flex w-full items-center gap-2 sm:w-auto"
          >
            {/* Slider labels are physics — "$\tau = RC$ (s)", "$\omega_0$" —
                and rendered as plain text they show the reader TeX source.
                Same treatment as every other label around a figure. */}
            <Label
              htmlFor={id}
              className="whitespace-nowrap text-secondary-foreground"
            >
              <MathText children={param.label ?? param.name} />
            </Label>
            <Slider
              id={id}
              className="min-w-24 flex-1 sm:w-32 sm:flex-initial"
              min={param.min}
              max={param.max}
              step={paramStep(param.min, param.max, param.step)}
              value={[value]}
              onValueChange={([next]) => {
                if (next !== undefined) onChange(param.name, next);
              }}
              aria-label={param.label ?? param.name}
            />
            <output
              htmlFor={id}
              className="min-w-[4.5ch] tabular-nums text-foreground"
            >
              {formatValue(value)}
            </output>
          </div>
        );
      })}

      <Button
        variant="ghost"
        size="xs"
        onClick={onReset}
        className="ml-auto text-muted-foreground hover:bg-brand-soft hover:text-primary"
      >
        reset
      </Button>
    </div>
  );
}
